<template>
    <div class="sidebar-menu">
        <div class="opener" @click="isMenuVisible = true">
            <i class="fal fa-clipboard-list"></i>
        </div>
        <SidebarComponent v-model="isMenuVisible" :width="550" :draggable="false" :showDim="props.showDim">
            <div class="sidebar-header">
                <PreorderFiltersHeader
                    :showCloseBtn="!props.showDim"
                    v-model:selectedDate="dateValue"
                    v-model:selectedShifts="selectedShiftIds"
                    :isLoading="isLoading"
                    :shifts="shifts"
                    @closeNavigation="isMenuVisible = false"
                />
            </div>

            <PreordersList
                :preorders="preorders"
                :isLoading="isLoading"
                :selectedShifts="selectedShifts"
                :defaultSelections="selectedTableReservationStates"
                :filterOptions="availableTableReservationStateFilterOptions"
                @filterSelectionChanged="onFilterSelectionChanged"
            />
        </SidebarComponent>
    </div>
</template>

<script setup lang="ts">
    import { Popup } from "@/app/ui/Popup";
    import { Factory } from "@/data/dao/Factory";
    import { OperatorDao } from "@/data/dao/service/OperatorDao";
    import { dataEvents } from "@/data/events/dataEvents";
    import type { IReservationTableAssignmentChangedEvent } from "@/data/model/Events";
    import type { IPreorderShift } from "@/data/model/Operator";
    import type { IPreorder } from "@/data/model/Order";
    import type { ILinkableEntity } from "@/data/model/Shared";
    import { operatorEvents } from "@/operator/events/operatorEvents";
    import { clientLanguage } from "@/shared/localization/language";
    import { logError } from "@/shared/logging/errors";
    import { computed, onBeforeUnmount, onMounted, ref, watch } from "vue";

    import SidebarComponent from "@/operator/components/layout/SidebarComponent.vue";
    import PreorderFiltersHeader from "@/operator/components/order/PreordersFilterHeader.vue";
    import PreordersList from "@/operator/components/order/PreordersList.vue";

    const operatorDao = Factory.getInstance<OperatorDao>(OperatorDao);

    const isMenuVisible = ref(false);
    const preorders = ref<IPreorder[]>([]);
    const shifts = ref<IPreorderShift[]>([]);
    const selectedShiftIds = ref<string[]>([]);
    const selectedShifts = computed(() => {
        return shifts.value.filter((shift) => selectedShiftIds.value.includes(shift.id));
    });
    const dateValue = ref<Date>(new Date());

    const availableTableReservationStateFilterOptions = ref<Array<ILinkableEntity>>([]);

    const defaultTableReservationStates = ref<ILinkableEntity[]>([]);
    const selectedTableReservationStates = ref<ILinkableEntity[]>([]);

    // Props
    const props = withDefaults(
        defineProps<{
            showDim: boolean;
        }>(),
        {
            showDim: true,
        }
    );

    watch(isMenuVisible, async (newValue) => {
        if (newValue) {
            const businessToday = await operatorDao.getPreorderBusinessToday({ localNow: new Date() });
            dateValue.value = businessToday;
            selectedTableReservationStates.value = defaultTableReservationStates.value;
            operatorEvents.sideMenu.onShow.dispatch({ selectedDate: businessToday });
        } else {
            operatorEvents.sideMenu.onHide.dispatch();
            preorders.value = [];
            shifts.value = [];
            selectedShiftIds.value = [];
            selectedTableReservationStates.value = defaultTableReservationStates.value;
        }
    });

    watch(dateValue, () => {
        loadData();
        operatorEvents.sideMenu.onChangedSelectedDate.dispatch({ selectedDate: dateValue.value });
    });

    const isLoading = ref(false);

    async function onFilterSelectionChanged(stateSelection: Array<ILinkableEntity>) {
        selectedTableReservationStates.value = stateSelection;

        await refreshData();
    }

    function resetSelectedTableReservationStates() {
        selectedTableReservationStates.value = defaultTableReservationStates.value;
    }

    async function loadData() {
        try {
            isLoading.value = true;
            const dateString = getDateString();
            shifts.value = await operatorDao.getPreorderShifts({ date: dateString });

            preorders.value = await operatorDao.getPreorders({
                date: dateString,
                filterTableReservationStates: getFilterStates(),
                roomId: undefined,
            });
        } catch (ex) {
            logError(ex);
            Popup.show("Failed to load data.");
        } finally {
            isLoading.value = false;
        }
    }

    async function loadTableReservationStates() {
        selectedTableReservationStates.value = [];

        try {
            const filter = await operatorDao.getTableReservationStateFilterOptions({
                language: clientLanguage(),
            });

            availableTableReservationStateFilterOptions.value = filter.filterOptions;
            defaultTableReservationStates.value = filter.filterOptions.filter(
                (opt) => filter.defaultSelections.findIndex((s) => s === opt.id) !== -1
            );

            resetSelectedTableReservationStates();
        } catch (error) {
            logError(error);
            Popup.show("Failed to load data.");
        }
    }

    async function refreshData() {
        try {
            isLoading.value = true;
            preorders.value = await operatorDao.getPreorders({
                date: getDateString(),
                roomId: undefined,
                filterTableReservationStates: getFilterStates(),
            });
        } catch (ex) {
            logError(ex);
            Popup.show("Failed to load data.");
        } finally {
            isLoading.value = false;
        }
    }

    function getDateString(): string {
        return dateValue.value.toISOString();
    }

    function getFilterStates(): Array<string> {
        return selectedTableReservationStates.value.map((c) => c.id);
    }

    function onShowOrderAndPayView() {
        isMenuVisible.value = false;
    }

    function onReservationTableAssignmentChanged(ev: IReservationTableAssignmentChangedEvent) {
        setTimeout(() => {
            refreshData();
        }, 2000);
    }

    onMounted(async () => {
        await loadTableReservationStates();

        dataEvents.operatorSocket.ReservationTableAssignmentChanged.add(onReservationTableAssignmentChanged);

        operatorEvents.showOrderAndPayView.add(onShowOrderAndPayView);
        //     operatorEvents.editPreorderPopup.preorderUpdated.add(refreshData);
        //     operatorEvents.roomPlanController.onRoomPlanLoaded.add(onRoomPlanChanged);
    });

    onBeforeUnmount(() => {
        operatorEvents.showOrderAndPayView.remove(onShowOrderAndPayView);
        dataEvents.operatorSocket.ReservationTableAssignmentChanged.remove(onReservationTableAssignmentChanged);

        //     operatorEvents.editPreorderPopup.preorderUpdated.remove(refreshData);
        //     operatorEvents.roomPlanController.onRoomPlanLoaded.remove(onRoomPlanChanged);
    });
</script>

<style lang="scss">
    @use "./../../../../assets/styles/scss/shared/_variables.scss";
    @use "./../../../../assets/styles/scss/shared/_mixins.scss";

    .sidebar-menu {
        > .opener {
            width: 38px;
            height: 34px;
            background-color: #a6d234;
            text-align: center;
            pointer-events: all;
            margin-left: 16px;
            cursor: pointer;
            border-radius: 5px;

            > .fal {
                color: #fff;
                font-size: 22px;
                line-height: 34px;
                display: inline-block;
                position: relative;
            }
        }

        .sidebar-nav {
            display: flex;
            flex-direction: column;
        }

        .reservations-list {
            flex: 1 1 auto;
        }

        .loading-placeholder {
            flex: 1 1 auto;

            .reservations-header {
                padding: 0.5em 1em;
                background-color: #484848;
                color: #fff;
                font-weight: 500;
                text-align: left;
            }

            .loading {
                padding-top: 0.5em;
                padding-left: 2em;
            }
        }

        .sidebar-header {
            position: relative;
            background-color: #8fb52c;
            min-height: 51px;
            display: flex;
            align-items: center;

            > .table-reservation-header {
                margin-left: -0.5em;
            }
        }
    }
</style>
